import React, { useEffect, useState } from "react"
import { Badge, Button, Form, Modal, Spinner, Table } from "react-bootstrap"
import { useTranslation } from "react-i18next"
import { toast } from "react-toastify"
import { APIService } from "@/common/API"
import { parsedUser } from "@/common/GetCurrentUser"

interface IncidentTypeRecord {
  id: string
  organizationId: string
  name: string
  department?: string
  description?: string
  created?: string
}

interface Props {
  show: boolean
  toggle: (section: string) => void
}

const IncidentTypeComponent: React.FC<Props> = ({ show, toggle }) => {
  const { t } = useTranslation("global")
  const user: any = parsedUser()

  const [records, setRecords] = useState<IncidentTypeRecord[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [isSeeding, setIsSeeding] = useState(false)
  const [isSaving, setIsSaving] = useState(false)

  // Edit / Add modal state
  const [showEditModal, setShowEditModal] = useState(false)
  const [editingItem, setEditingItem] = useState<Partial<IncidentTypeRecord>>({
    name: "",
    department: "",
    description: "",
  })

  useEffect(() => {
    if (!show) return
    loadRecords()
  }, [show])

  const loadRecords = async () => {
    setIsLoading(true)
    try {
      const res = await APIService.getAction(
        "IncidentType/List",
        `organizationId=${user.OrganizationId}`
      )
      if (res?.data?.status === "ok") {
        setRecords(res.data.response || [])
      }
    } catch (e) {
      toast.error(t("Error al cargar los tipos de incidencias"))
    } finally {
      setIsLoading(false)
    }
  }

  const handleOpenAdd = () => {
    setEditingItem({
      id: "",
      organizationId: user.OrganizationId,
      name: "",
      department: "",
      description: "",
    })
    setShowEditModal(true)
  }

  const handleOpenEdit = (item: IncidentTypeRecord) => {
    setEditingItem({ ...item })
    setShowEditModal(true)
  }

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!editingItem.name?.trim()) {
      toast.warn(t("El nombre del tipo de incidencia es obligatorio"))
      return
    }

    setIsSaving(true)
    try {
      const payload = {
        id: editingItem.id || "",
        organizationId: user.OrganizationId,
        name: editingItem.name.trim(),
        department: editingItem.department?.trim() || null,
        description: editingItem.description?.trim() || null,
      }

      const res = await APIService.postAction("IncidentType/AddEdit", payload)
      if (res?.data?.status === "ok") {
        toast.success(
          editingItem.id
            ? t("Tipo de incidencia actualizado")
            : t("Tipo de incidencia creado exitosamente")
        )
        setShowEditModal(false)
        await loadRecords()
      } else {
        toast.error(res?.data?.details || t("Error al guardar"))
      }
    } catch (e: any) {
      toast.error(e?.message || t("Error de conexión al guardar"))
    } finally {
      setIsSaving(false)
    }
  }

  const handleDelete = async (id: string, name: string) => {
    if (
      !window.confirm(
        t("¿Estás seguro de eliminar el tipo de incidencia \"{{name}}\"?", { name })
      )
    ) {
      return
    }

    try {
      const res = await APIService.getAction("IncidentType/Delete", `id=${id}`)
      if (res?.data?.status === "ok") {
        toast.info(t("Tipo de incidencia eliminado"))
        setRecords(prev => prev.filter(r => r.id !== id))
      } else {
        toast.error(res?.data?.details || t("Error al eliminar"))
      }
    } catch (e) {
      toast.error(t("Error al eliminar el registro"))
    }
  }

  const handleSeedDefaults = async () => {
    if (
      !window.confirm(
        t(
          "¿Deseas inicializar el catálogo de tu empresa con los 12 tipos predeterminados del sistema?"
        )
      )
    ) {
      return
    }

    setIsSeeding(true)
    try {
      const res = await APIService.postAction(
        `IncidentType/SeedDefaults?organizationId=${user.OrganizationId}`,
        {}
      )
      if (res?.data?.status === "ok") {
        toast.success(
          res.data.details || t("Catálogo inicial importado exitosamente")
        )
        await loadRecords()
      } else {
        toast.error(res?.data?.details || t("Error al importar el catálogo base"))
      }
    } catch (e: any) {
      toast.error(e?.message || t("Error al conectar con el servidor"))
    } finally {
      setIsSeeding(false)
    }
  }

  return (
    <>
      <Modal show={show} onHide={() => toggle("")} size="lg" centered>
        <Modal.Header closeButton className="bg-light">
          <div>
            <Modal.Title className="h5 fw-bold mb-1 text-dark">
              <i className="fas fa-tags text-warning me-2" />
              {t("Tipos de Incidencias & Reclamaciones")}
            </Modal.Title>
            <div className="text-muted small">
              {t(
                "Configura y personaliza los tipos de incidencias permitidos para el reporte en campo de tu empresa."
              )}
            </div>
          </div>
        </Modal.Header>

        <Modal.Body className="p-4">
          <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-3 pb-2 border-bottom">
            <div className="small text-secondary">
              <strong>{records.length}</strong> {t("tipos configurados para tu empresa")}
            </div>
            <div className="d-flex gap-2">
              {records.length === 0 && (
                <Button
                  variant="outline-secondary"
                  size="sm"
                  onClick={handleSeedDefaults}
                  disabled={isSeeding || isLoading}
                >
                  {isSeeding ? (
                    <>
                      <Spinner size="sm" className="me-1" /> {t("Importando...")}
                    </>
                  ) : (
                    <>
                      <i className="fas fa-magic me-1 text-warning" />
                      {t("Cargar Catálogo Base (12 Tipos)")}
                    </>
                  )}
                </Button>
              )}
              <Button variant="primary" size="sm" onClick={handleOpenAdd}>
                <i className="fas fa-plus me-1" />
                {t("Nuevo Tipo")}
              </Button>
            </div>
          </div>

          {isLoading ? (
            <div className="text-center py-5">
              <Spinner animation="border" variant="primary" />
              <div className="text-muted small mt-2">{t("Cargando catálogo...")}</div>
            </div>
          ) : records.length === 0 ? (
            <div className="text-center py-5 bg-light rounded-3 p-4">
              <div
                className="bg-warning bg-opacity-10 text-warning rounded-circle p-3 mb-3 d-inline-flex align-items-center justify-content-center"
                style={{ width: 56, height: 56 }}
              >
                <i className="fas fa-tags fs-3" />
              </div>
              <h6 className="fw-bold text-dark mb-1">
                {t("Sin tipos de incidencia configurados")}
              </h6>
              <p className="text-muted small mb-3" style={{ maxWidth: 420, margin: "0 auto" }}>
                {t(
                  "Tu empresa está utilizando los tipos predeterminados del sistema. Puedes cargar los 12 tipos base con un clic para empezar a editarlos o agregar los tuyos desde cero."
                )}
              </p>
              <Button
                variant="primary"
                size="sm"
                onClick={handleSeedDefaults}
                disabled={isSeeding}
              >
                {isSeeding ? (
                  <>
                    <Spinner size="sm" className="me-1" /> {t("Importando...")}
                  </>
                ) : (
                  <>
                    <i className="fas fa-magic me-1" />
                    {t("Inicializar Catálogo Base")}
                  </>
                )}
              </Button>
            </div>
          ) : (
            <div className="table-responsive">
              <Table hover className="align-middle mb-0">
                <thead className="table-light extra-small text-uppercase text-muted">
                  <tr>
                    <th>{t("Tipo de Incidencia")}</th>
                    <th>{t("Departamento")}</th>
                    <th>{t("Descripción / Criterio")}</th>
                    <th className="text-end" style={{ width: 110 }}>
                      {t("Acciones")}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {records.map(r => (
                    <tr key={r.id}>
                      <td>
                        <span className="fw-semibold text-dark">{r.name}</span>
                      </td>
                      <td>
                        {r.department ? (
                          <Badge bg="light" text="dark" className="border font-monospace">
                            <i className="fas fa-building me-1 text-muted" />
                            {r.department}
                          </Badge>
                        ) : (
                          <span className="text-muted extra-small">
                            {t("Todos")}
                          </span>
                        )}
                      </td>
                      <td>
                        <span className="text-muted small">
                          {r.description || "-"}
                        </span>
                      </td>
                      <td className="text-end">
                        <Button
                          variant="light"
                          size="sm"
                          className="me-1 text-primary border-0"
                          onClick={() => handleOpenEdit(r)}
                          title={t("Editar")}
                        >
                          <i className="fas fa-pencil-alt" />
                        </Button>
                        <Button
                          variant="light"
                          size="sm"
                          className="text-danger border-0"
                          onClick={() => handleDelete(r.id, r.name)}
                          title={t("Eliminar")}
                        >
                          <i className="fas fa-trash-alt" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          )}
        </Modal.Body>

        <Modal.Footer className="bg-light d-flex justify-content-between">
          <div className="text-muted extra-small">
            <i className="fas fa-info-circle me-1" />
            {t("Los cambios se sincronizan en tiempo real con la aplicación móvil")}
          </div>
          <Button variant="secondary" size="sm" onClick={() => toggle("")}>
            {t("Cerrar")}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Form modal for Add / Edit */}
      <Modal
        show={showEditModal}
        onHide={() => setShowEditModal(false)}
        centered
        backdrop="static"
      >
        <Form onSubmit={handleSave}>
          <Modal.Header closeButton>
            <Modal.Title className="h6 fw-bold mb-0 text-dark">
              {editingItem.id
                ? t("Editar Tipo de Incidencia")
                : t("Nuevo Tipo de Incidencia")}
            </Modal.Title>
          </Modal.Header>

          <Modal.Body className="p-4">
            <Form.Group className="mb-3">
              <Form.Label className="fw-semibold small">
                {t("Nombre del Tipo de Incidencia")}{" "}
                <span className="text-danger">*</span>
              </Form.Label>
              <Form.Control
                type="text"
                placeholder={t("Ej: Mercancía averiada, Retraso en entrega...")}
                value={editingItem.name || ""}
                onChange={e =>
                  setEditingItem(prev => ({ ...prev, name: e.target.value }))
                }
                required
                autoFocus
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label className="fw-semibold small">
                {t("Departamento Asociado (Opcional)")}
              </Form.Label>
              <Form.Control
                type="text"
                placeholder={t("Ej: Almacén, Ventas Externas, Showroom...")}
                value={editingItem.department || ""}
                onChange={e =>
                  setEditingItem(prev => ({
                    ...prev,
                    department: e.target.value,
                  }))
                }
              />
              <Form.Text className="text-muted extra-small">
                {t("Dejar vacío para que aplique a cualquier departamento")}
              </Form.Text>
            </Form.Group>

            <Form.Group className="mb-2">
              <Form.Label className="fw-semibold small">
                {t("Descripción o Criterio de Reporte")}
              </Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                placeholder={t(
                  "Describe cuándo los agentes deben seleccionar este tipo..."
                )}
                value={editingItem.description || ""}
                onChange={e =>
                  setEditingItem(prev => ({
                    ...prev,
                    description: e.target.value,
                  }))
                }
              />
            </Form.Group>
          </Modal.Body>

          <Modal.Footer>
            <Button
              variant="outline-secondary"
              size="sm"
              onClick={() => setShowEditModal(false)}
              disabled={isSaving}
            >
              {t("Cancelar")}
            </Button>
            <Button
              variant="primary"
              size="sm"
              type="submit"
              disabled={isSaving}
            >
              {isSaving ? (
                <>
                  <Spinner size="sm" className="me-1" /> {t("Guardando...")}
                </>
              ) : (
                t("Guardar Tipo")
              )}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </>
  )
}

export default IncidentTypeComponent
